import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { CarouselScrollBehaviour } from '@freelancer/ui/carousel';
import {
  PictureDisplay,
  PictureLoadMethod,
  PictureObjectFit,
} from '@freelancer/ui/picture';
import { SpinnerColor, SpinnerSize } from '@freelancer/ui/spinner';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ActiveColor, AspectRatio, CaptionType } from './section-gallery.types';

@Component({
  selector: 'fl-section-gallery-item',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionGalleryItemComponent {
  @Input() src: string;
  @Input() srcTablet?: string;
  @Input() srcDesktop?: string;
  @Input() thumbnailSrc?: string;
  @Input() alt: string;

  isPreviewReady = false;
  isThumbnailReady = false;

  @ContentChild('imageCaption')
  imageCaption: TemplateRef<any>;
}

@Component({
  selector: 'fl-section-gallery',
  template: `
    <fl-carousel
      [flMarginBottom]="'mid'"
      [currentIndex]="(index$ | flAsync) || 0"
      [showDotIndicator]="showDotIndicator"
      (slideChange)="select($event)"
    >
      <fl-carousel-item
        *ngFor="let item of galleryItems; trackBy: trackByAlt"
        (mouseover)="handleHover(true)"
        (mouseleave)="handleHover(false)"
      >
        <div
          class="MainCarousel"
          [attr.data-aspect-ratio]="aspectRatio"
        >
          <div class="MainCarousel-item">
            <fl-spinner
              *ngIf="!item.isPreviewReady"
              class="MainCarousel-loadingState"
              [color]="SpinnerColor.LIGHT"
              [size]="SpinnerSize.MID"
            ></fl-spinner>
            <fl-picture
              [class.MainCarousel-item--hidden]="!item.isPreviewReady"
              [alt]="item.alt"
              [boundedWidth]="true"
              [boundedHeight]="boundedHeight"
              [display]="PictureDisplay.BLOCK"
              [loadMethod]="PictureLoadMethod.LAZY"
              [src]="item.src"
              [srcTablet]="item.srcTablet"
              [srcDesktop]="item.srcDesktop"
              [fullWidth]="true"
              [objectFit]="PictureObjectFit.COVER"
              (imageLoaded)="item.isPreviewReady = true"
            ></fl-picture>
            <div
              *ngIf="item.imageCaption && item.isPreviewReady"
              class="MainCarousel-caption"
              [flMarginRight]="'xlarge'"
              [flMarginBottom]="'xlarge'"
              [flShowDesktop]="true"
              [attr.data-caption-type]="captionType"
            >
              <ng-container
                *ngTemplateOutlet="item.imageCaption"
              ></ng-container>
            </div>
          </div>
        </div>
      </fl-carousel-item>
    </fl-carousel>
    <fl-carousel
      [slidesToShow]="thumbnailsToShow"
      [scrollBehaviour]="CarouselScrollBehaviour.FREE"
      [currentIndex]="(index$ | flAsync) || 0"
      [padding]="8"
    >
      <fl-carousel-item
        #thumbnail
        *ngFor="
          let item of galleryItems;
          let i = index;
          let last = last;
          trackBy: trackByAlt
        "
        (click)="select(i)"
      >
        <fl-button
          class="Thumbnails-btn"
          [display]="'block'"
          [class.Thumbnails-btn--last]="last"
          [attr.data-active]="(index$ | flAsync) === i"
          [attr.data-active-color]="activeColor"
        >
          <fl-loading-text
            *ngIf="!item.isThumbnailReady"
            class="Thumbnails-btn-loadingState"
            [padded]="false"
          ></fl-loading-text>

          <fl-picture
            class="Thumbnails-btn-picture"
            [boundedWidth]="true"
            [boundedHeight]="boundedHeight"
            [fullWidth]="true"
            [display]="PictureDisplay.BLOCK"
            [loadMethod]="PictureLoadMethod.LAZY"
            [alt]="item.alt"
            [src]="item.thumbnailSrc || item.src"
            [objectFit]="PictureObjectFit.COVER"
            (imageLoaded)="item.isThumbnailReady = true"
          ></fl-picture>
        </fl-button>
      </fl-carousel-item>
    </fl-carousel>
  `,
  styleUrls: ['./section-gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SectionGalleryComponent implements AfterViewInit, OnDestroy {
  PictureDisplay = PictureDisplay;
  PictureObjectFit = PictureObjectFit;
  CarouselScrollBehaviour = CarouselScrollBehaviour;
  SpinnerColor = SpinnerColor;
  SpinnerSize = SpinnerSize;
  PictureLoadMethod = PictureLoadMethod;

  @Input() activeColor = ActiveColor.PRIMARY;
  @Input() aspectRatio = AspectRatio.DEFAULT;
  @Input() boundedHeight = true;
  @Input() captionType = CaptionType.DEFAULT;
  @Input() thumbnailsToShow = 4;
  @Input() showDotIndicator = false;

  @Output() hover = new EventEmitter<boolean>(false);

  @ContentChildren(SectionGalleryItemComponent)
  galleryItems: QueryList<SectionGalleryItemComponent>;
  @ViewChild('thumbnailsContainer', { read: ElementRef })
  container: ElementRef;
  @ViewChildren('thumbnail', { read: ElementRef })
  thumbnails: QueryList<ElementRef>;

  private _indexSubject$ = new BehaviorSubject(0);
  index$ = this._indexSubject$.asObservable();

  private subscriptions = new Subscription();

  select(index: number): void {
    this._indexSubject$.next(index);
  }

  trackByAlt(_: number, item: SectionGalleryItemComponent): string {
    return item.alt;
  }

  handleHover(value: boolean): void {
    this.hover.emit(value);
  }

  ngAfterViewInit(): void {
    // Reset the index to 0 when number of gallery items has changed
    this.subscriptions.add(
      this.galleryItems.changes.subscribe(item => {
        this._indexSubject$.next(0);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
